import { useEffect, useRef } from 'react';

import { useFeature } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';

export type RevealSubmission = {
	id: string;
	viewer: { party: graphql.EvaluationSubmissionParty; hasShared: boolean };
	employee: { user: { firstName: string; lastName: string } };
};

export function useEvaluationSubmissionReveal(submission: RevealSubmission) {
	const featureEnabled = useFeature(graphql.FeatureName.EvaluationReveal);

	const { data, refetch } = graphql.useEvaluationRevealQuery({
		variables: { submissionID: submission.id },
		skip: !featureEnabled,
	});

	const [markSeen] = graphql.useMarkRevealSeenMutation();

	const reveal = data?.evaluationReveal;
	const hasFields = (reveal?.fields.length ?? 0) > 0;
	const isEmployee = submission.viewer.party === graphql.EvaluationSubmissionParty.Employee;
	const seenSubmissionRef = useRef<string | undefined>(undefined);

	// Sharing changes the reveal's visibility; refetch when the viewer's
	// share state flips so the lock icon updates without a page reload. This
	// catches every share path (share modal, sign flow), because they all end
	// up updating the submission fragment in the cache.
	const hasShared = submission.viewer.hasShared;
	const previousHasSharedRef = useRef(hasShared);
	useEffect(() => {
		if (featureEnabled && hasShared && !previousHasSharedRef.current) {
			refetch().catch(() => undefined);
		}
		previousHasSharedRef.current = hasShared;
	}, [featureEnabled, hasShared, refetch]);

	useEffect(() => {
		if (
			reveal?.visibleToEmployee &&
			hasFields &&
			isEmployee &&
			seenSubmissionRef.current !== submission.id
		) {
			seenSubmissionRef.current = submission.id;
			// Best-effort: a failed markSeen should never break the page.
			markSeen({ variables: { submissionID: submission.id } }).catch(() => undefined);
		}
	}, [reveal?.visibleToEmployee, hasFields, isEmployee, submission.id, markSeen]);

	const visible = featureEnabled && hasFields;

	return { reveal: visible ? reveal : undefined };
}
